/* ==========================================================================
   FILE: global/layouts/AppLayout.jsx
   IAMONEAI - Main Application Layout
   Three-panel dashboard: Identity | Chat | Mind
   Responsive: Desktop 3-panel, Mobile single panel with bottom nav
   ========================================================================== */

import React, { useState, useEffect } from 'react';
import { useApp } from '../context';
import { useBreakpoint } from '../hooks';
import MobileNav from './MobileNav';
import './AppLayout.css';

// ==========================================================================
// PANEL IDS
// ==========================================================================

export const PANELS = {
  IDENTITY: 'identity',
  CHAT: 'chat',
  MIND: 'mind',
};

// ==========================================================================
// APP LAYOUT COMPONENT
// ==========================================================================

/**
 * Main application layout with three panels
 * 
 * Desktop: Side-by-side panels (15% | 60% | 25%)
 * Mobile: Single panel with bottom navigation
 * 
 * @param {Object} props
 * @param {React.ReactNode} identityPanel - Left panel content (Identity/Profile)
 * @param {React.ReactNode} chatPanel - Center panel content (Chat)
 * @param {React.ReactNode} mindPanel - Right panel content (Mind/Brain)
 * @param {React.ReactNode} header - Optional header content
 * 
 * @example
 * <AppLayout
 *   identityPanel={<IdentityPanel />}
 *   chatPanel={<ChatPanel />}
 *   mindPanel={<MindPanel />}
 * />
 */
export function AppLayout({
  identityPanel,
  chatPanel,
  mindPanel,
  header,
}) {
  const { isAuthenticated, activeContext } = useApp();
  const { isMobile, isTablet } = useBreakpoint();
  const [activePanel, setActivePanel] = useState(PANELS.CHAT);
  const [isPanelCollapsed, setIsPanelCollapsed] = useState({
    identity: false,
    mind: false,
  });

  // On mobile, default to chat panel
  useEffect(() => {
    if (isMobile) {
      setActivePanel(PANELS.CHAT);
    }
  }, [isMobile]);

  // Get context color for theming
  const contextColors = {
    personal: 'var(--context-personal)',
    work: 'var(--context-work)',
    family: 'var(--context-family)',
  };

  const contextColor = contextColors[activeContext] || contextColors.personal;

  // Toggle panel collapse (tablet view)
  const togglePanel = (panel) => {
    setIsPanelCollapsed(prev => ({
      ...prev,
      [panel]: !prev[panel],
    }));
  };

  // ----- Mobile Layout -----
  if (isMobile) {
    return (
      <div 
        className="app-layout app-layout-mobile"
        style={{ '--active-context-color': contextColor }}
      >
        {/* Header */}
        {header && (
          <header className="app-layout-header">
            {header}
          </header>
        )}

        {/* Single Panel Content */}
        <main className="app-layout-main-mobile">
          <div className={`mobile-panel ${activePanel === PANELS.IDENTITY ? 'mobile-panel-active' : ''}`}>
            {identityPanel}
          </div>
          <div className={`mobile-panel ${activePanel === PANELS.CHAT ? 'mobile-panel-active' : ''}`}>
            {chatPanel}
          </div>
          <div className={`mobile-panel ${activePanel === PANELS.MIND ? 'mobile-panel-active' : ''}`}>
            {mindPanel}
          </div>
        </main>

        {/* Bottom Navigation */}
        <MobileNav
          activePanel={activePanel}
          onPanelChange={setActivePanel}
          contextColor={contextColor}
        />
      </div>
    );
  }

  // ----- Tablet Layout -----
  if (isTablet) {
    return (
      <div 
        className="app-layout app-layout-tablet"
        style={{ '--active-context-color': contextColor }}
      >
        {/* Header */}
        {header && (
          <header className="app-layout-header">
            {header}
          </header>
        )}

        <div className="app-layout-body">
          {/* Identity Panel (Collapsible) */}
          <aside 
            className={`app-panel app-panel-identity ${isPanelCollapsed.identity ? 'app-panel-collapsed' : ''}`}
          >
            <button
              className="panel-collapse-btn"
              onClick={() => togglePanel('identity')}
              aria-label={isPanelCollapsed.identity ? 'Expand identity panel' : 'Collapse identity panel'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d={isPanelCollapsed.identity ? 'm9 18 6-6-6-6' : 'm15 18-6-6 6-6'} />
              </svg>
            </button>
            <div className="panel-content">
              {identityPanel}
            </div>
          </aside>

          {/* Chat Panel (Main) */}
          <main className="app-panel app-panel-chat">
            {chatPanel}
          </main>

          {/* Mind Panel (Collapsible) */}
          <aside 
            className={`app-panel app-panel-mind ${isPanelCollapsed.mind ? 'app-panel-collapsed' : ''}`}
          >
            <button
              className="panel-collapse-btn panel-collapse-btn-right"
              onClick={() => togglePanel('mind')}
              aria-label={isPanelCollapsed.mind ? 'Expand mind panel' : 'Collapse mind panel'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d={isPanelCollapsed.mind ? 'm15 18-6-6 6-6' : 'm9 18 6-6-6-6'} />
              </svg>
            </button>
            <div className="panel-content">
              {mindPanel}
            </div>
          </aside>
        </div>
      </div>
    );
  }

  // ----- Desktop Layout -----
  return (
    <div 
      className="app-layout app-layout-desktop"
      style={{ '--active-context-color': contextColor }}
    >
      {/* Header */}
      {header && (
        <header className="app-layout-header">
          {header}
        </header>
      )}

      <div className="app-layout-body">
        {/* Identity Panel (Left - 15%) */}
        <aside className="app-panel app-panel-identity">
          <div className="panel-content">
            {identityPanel}
          </div>
        </aside>

        {/* Chat Panel (Center - 60%) */}
        <main className="app-panel app-panel-chat">
          {chatPanel}
        </main>

        {/* Mind Panel (Right - 25%) */}
        <aside className="app-panel app-panel-mind">
          <div className="panel-content">
            {mindPanel}
          </div>
        </aside>
      </div>
    </div>
  );
}

// ==========================================================================
// PANEL HEADER COMPONENT
// ==========================================================================

/**
 * Consistent header for each panel
 * 
 * @example
 * <PanelHeader
 *   title="Identity"
 *   icon={<UserIcon />}
 *   action={<Button size="sm">Edit</Button>}
 * />
 */
export function PanelHeader({
  title,
  subtitle,
  icon,
  action,
  className = '',
}) {
  const headerClasses = [
    'panel-header',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={headerClasses}>
      <div className="panel-header-content">
        {icon && <span className="panel-header-icon">{icon}</span>}
        <div className="panel-header-text">
          <h2 className="panel-header-title">{title}</h2>
          {subtitle && <p className="panel-header-subtitle">{subtitle}</p>}
        </div>
      </div>
      {action && <div className="panel-header-action">{action}</div>}
    </div>
  );
}

// ==========================================================================
// PANEL SECTION COMPONENT
// ==========================================================================

/**
 * Section within a panel with optional title
 * 
 * @example
 * <PanelSection title="Guardrails">
 *   <GuardrailsList />
 * </PanelSection>
 */
export function PanelSection({
  title,
  action,
  children,
  collapsible = false,
  defaultCollapsed = false,
  className = '',
}) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  const sectionClasses = [
    'panel-section',
    collapsible && 'panel-section-collapsible',
    isCollapsed && 'panel-section-collapsed',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={sectionClasses}>
      {title && (
        <div 
          className="panel-section-header"
          onClick={collapsible ? () => setIsCollapsed(!isCollapsed) : undefined}
          role={collapsible ? 'button' : undefined}
          tabIndex={collapsible ? 0 : undefined}
          onKeyDown={collapsible ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsCollapsed(!isCollapsed);
            }
          } : undefined}
        >
          <h3 className="panel-section-title">{title}</h3>
          <div className="panel-section-actions">
            {action}
            {collapsible && (
              <span className={`panel-section-chevron ${isCollapsed ? 'panel-section-chevron-collapsed' : ''}`}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </span>
            )}
          </div>
        </div>
      )}
      {!isCollapsed && (
        <div className="panel-section-content">
          {children}
        </div>
      )}
    </div>
  );
}

// ==========================================================================
// PANEL SCROLL AREA
// ==========================================================================

/**
 * Scrollable area within a panel
 * 
 * @example
 * <PanelScrollArea>
 *   <MemoryList memories={memories} />
 * </PanelScrollArea>
 */
export function PanelScrollArea({
  children,
  className = '',
}) {
  return (
    <div className={`panel-scroll-area ${className}`.trim()}>
      {children}
    </div>
  );
}

// ==========================================================================
// APP HEADER COMPONENT
// ==========================================================================

/**
 * Main app header with logo, context switcher, and user menu
 * 
 * @example
 * <AppHeader
 *   onMenuClick={() => setMenuOpen(true)}
 *   onSettingsClick={() => navigate('/settings')}
 * />
 */
export function AppHeader({
  onMenuClick,
  onSettingsClick,
  onProfileClick,
  showContextSwitcher = true,
}) {
  const { user, activeContext } = useApp();
  const { isMobile } = useBreakpoint();

  return (
    <div className="app-header">
      {/* Left: Logo & Menu */}
      <div className="app-header-left">
        {isMobile && onMenuClick && (
          <button
            className="app-header-menu-btn"
            onClick={onMenuClick}
            aria-label="Open menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        )}
        
        <div className="app-header-logo">
          <span className="app-header-logo-icon">🧠</span>
          <span className="app-header-logo-text">IAMONEAI</span>
        </div>
      </div>

      {/* Center: Context (Desktop only) */}
      {!isMobile && showContextSwitcher && (
        <div className="app-header-center">
          {/* Context switcher will be rendered by parent */}
        </div>
      )}

      {/* Right: Actions */}
      <div className="app-header-right">
        {onSettingsClick && (
          <button
            className="app-header-action-btn"
            onClick={onSettingsClick}
            aria-label="Settings"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </button>
        )}

        {onProfileClick && user && (
          <button
            className="app-header-profile-btn"
            onClick={onProfileClick}
            aria-label="Profile"
          >
            <span className="app-header-avatar">
              {user.firstName?.[0] || user.name?.[0] || '?'}
            </span>
          </button>
        )}
      </div>
    </div>
  );
}

// ==========================================================================
// EXPORTS
// ==========================================================================

export default AppLayout;